import { Button, PageHeader, RoleChip, StatusChip } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { OwnerDataTable as DataTable } from "../../components/owner-data-table.tsx";
import {
  useAssignRoles,
  useCreateStaff,
  useOwnerState,
  useResetStaffPin,
  useUpdateStaff,
} from "../../features/backoffice/queries.ts";

export const Route = createFileRoute("/_app/staff")({ component: StaffScreen });

function StaffScreen() {
  const { data } = useOwnerState();
  const create = useCreateStaff();
  const assign = useAssignRoles();
  const updateStaff = useUpdateStaff();
  const resetPin = useResetStaffPin();

  function handleResetPin(staffId: string, staffName: string): void {
    resetPin.mutate(staffId, {
      onSuccess: (result) => {
        const { generatedPin } = result as { generatedPin: string };
        // One-time PIN handoff (FR-STAFF-002/FR-OWN-132): shown once, never
        // persisted client-side or logged — mirrors the platform
        // owner-credential-reset one-time-password pattern.
        window.alert(
          `New PIN for ${staffName}: ${generatedPin}\n\nShare this once — it will not be shown again.`,
        );
      },
      onError: () => {
        window.alert(`Could not reset the PIN for ${staffName}. Please try again.`);
      },
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff accounts"
        subtitle="Create/edit staff, PIN reset, active toggle, branch scope, and role assignment."
        actions={
          <Button
            onClick={() =>
              create.mutate({
                name: "New Runner",
                username: "runner",
                staffCode: "RUN",
                roles: ["runner"],
              })
            }
          >
            Create staff
          </Button>
        }
      />
      <DataTable
        rows={data?.staff ?? []}
        getRowKey={(row) => row.id}
        columns={[
          { key: "name", header: "Name", cell: (row) => `${row.name} (${row.username})` },
          { key: "code", header: "Staff code", cell: (row) => row.staffCode },
          {
            key: "roles",
            header: "Roles",
            cell: (row) => (
              <div className="flex flex-wrap gap-1">
                {row.roles.map((role) => (
                  <RoleChip key={role} label={role} />
                ))}
              </div>
            ),
          },
          { key: "branch", header: "Branch scope", cell: (row) => row.branchScope.join(", ") },
          {
            key: "active",
            header: "State",
            cell: (row) => (
              <StatusChip tone={row.active ? "success" : "neutral"}>
                {row.active ? "active" : "disabled"}
              </StatusChip>
            ),
          },
          {
            key: "actions",
            header: "Actions",
            cell: (row) => (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => assign.mutate({ staffId: row.id, roles: ["waiter", "runner"] })}
                >
                  Assign waiter+runner
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-foreground"
                  disabled={resetPin.isPending}
                  onClick={() => handleResetPin(row.id, row.name)}
                >
                  Reset PIN
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-foreground"
                  onClick={() =>
                    updateStaff.mutate({ staffId: row.id, input: { active: !row.active } })
                  }
                >
                  {row.active ? "Disable" : "Activate"}
                </Button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
